import type {
  AutoApplyActivityRecord,
  AutoApplyOverview,
  AutoApplyRuleSummary,
  ApplyToJobInput,
  CatalogApplyInput,
  CatalogApplyPreparation,
  CatalogApplySubmissionResult,
  CatalogApplicationRecord,
  CatalogJobDiscoveryResult,
  CatalogJobDetail,
  CatalogJobListItem,
  CatalogJobUserStateSummary,
  CatalogTrackRedirectInput,
  CatalogConfirmApplyInput,
  CompanyJobListResult,
  CompanyJobSummary,
  CreateCompanyJobInput,
  GenerateCompanyJobDraftInput,
  JobDistribution,
  EmployerApplicantDetail,
  EmployerApplicantListResult,
  EmployerApplicantMessageInput,
  EmployerApplicationStatusUpdateInput,
  JobApplicationSummary,
  JobDiscoveryDatePosted,
  JobDiscoveryItem,
  UpdateAutoApplyRuleInput,
} from "@tabbio/types";
import type {
  CompanyJobWithV2,
  CreateEmployerJobInput,
  GenerateCompanyJobDraftResultWithV2,
  UpdateEmployerJobInput,
} from "./employer-jobs-contract";
import { api } from "./api";
import { ApplyRequestError } from "./apply-error";

export interface DiscoverJobsParams {
  query?: string;
  locations?: string[];
  workModes?: ("onsite" | "hybrid" | "remote")[];
  employmentTypes?: string[];
  datePosted?: JobDiscoveryDatePosted;
  page?: number;
  limit?: number;
}

export interface FetchApplicationsParams {
  jobId?: string;
  limit?: number;
}

export interface CatalogDiscoverJobsParams {
  query?: string;
  locations?: string[];
  cities?: string[];
  workModes?: ("onsite" | "hybrid" | "remote")[];
  employmentTypes?: string[];
  sources?: string[];
  applyModes?: (
    | "tabbio_native"
    | "external_redirect"
    | "partner_ats_managed"
  )[];
  datePosted?: JobDiscoveryDatePosted;
  page?: number;
  limit?: number;
  cvId?: string;
}

export interface CatalogExploreJobsParams extends CatalogDiscoverJobsParams {
  includeLowConfidence?: boolean;
  surfaceEligibleOnly?: boolean;
}

export interface FetchEmployerApplicantsParams {
  query?: string;
  statuses?: (
    | "applied"
    | "reviewed"
    | "shortlisted"
    | "interview"
    | "offered"
    | "rejected"
    | "withdrawn"
  )[];
  page?: number;
  pageSize?: number;
}

function appendArrayParam(
  params: URLSearchParams,
  key: string,
  values?: string[],
) {
  if (!values?.length) {
    return;
  }

  params.set(key, values.join(","));
}

function appendMaybeParam(
  params: URLSearchParams,
  key: string,
  value?: string | number | boolean | null,
) {
  if (value === undefined || value === null || value === "") {
    return;
  }

  params.set(key, String(value));
}

function unwrapMeta(meta?: {
  page?: number;
  limit?: number;
  total?: number;
  hasMore?: boolean;
  noProfileFit?: boolean;
}) {
  return {
    page: meta?.page ?? 1,
    limit: meta?.limit ?? 20,
    total: meta?.total ?? 0,
    hasMore: meta?.hasMore ?? false,
    noProfileFit: meta?.noProfileFit ?? false,
  };
}

export function serializeCatalogDiscoverParams(
  params: CatalogDiscoverJobsParams = {},
) {
  return JSON.stringify({
    query: params.query ?? "",
    locations: params.locations ?? [],
    cities: params.cities ?? [],
    workModes: params.workModes ?? [],
    employmentTypes: params.employmentTypes ?? [],
    sources: params.sources ?? [],
    applyModes: params.applyModes ?? [],
    datePosted: params.datePosted ?? null,
    cvId: params.cvId ?? null,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  });
}

export function serializeCatalogExploreParams(
  params: CatalogExploreJobsParams = {},
) {
  return JSON.stringify({
    query: params.query ?? "",
    locations: params.locations ?? [],
    cities: params.cities ?? [],
    workModes: params.workModes ?? [],
    employmentTypes: params.employmentTypes ?? [],
    sources: params.sources ?? [],
    applyModes: params.applyModes ?? [],
    datePosted: params.datePosted ?? null,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
    includeLowConfidence: params.includeLowConfidence ?? true,
    surfaceEligibleOnly: params.surfaceEligibleOnly ?? false,
  });
}

export async function fetchCatalogDiscoveredJobs(
  input: CatalogDiscoverJobsParams = {},
): Promise<CatalogJobDiscoveryResult> {
  const params = new URLSearchParams();

  appendMaybeParam(params, "query", input.query?.trim() || null);
  appendArrayParam(params, "locations", input.locations);
  appendArrayParam(params, "cities", input.cities);
  appendArrayParam(params, "workModes", input.workModes);
  appendArrayParam(params, "employmentTypes", input.employmentTypes);
  appendArrayParam(params, "sources", input.sources);
  appendArrayParam(params, "applyModes", input.applyModes);
  appendMaybeParam(params, "datePosted", input.datePosted ?? null);
  appendMaybeParam(params, "page", input.page ?? null);
  appendMaybeParam(params, "limit", input.limit ?? null);
  appendMaybeParam(params, "cvId", input.cvId ?? null);

  const res = await api.get<CatalogJobListItem[]>(
    `/api/job-catalog/discover${params.size ? `?${params.toString()}` : ""}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  return {
    jobs: res.data ?? [],
    ...unwrapMeta(res.meta),
  };
}

export interface CatalogRefreshResult {
  /** Postings that did not exist in the catalog before this fetch. */
  added: number;
  updated: number;
  fetched: number;
  queries: number;
}

/** Keep the server error code so Jobs can route entitlement failures correctly. */
export class CatalogRefreshError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = "CatalogRefreshError";
  }
}

/** Searches live boards for the signed-in user's roles. Billing stays server-side. */
export async function refreshCatalogJobs(): Promise<CatalogRefreshResult> {
  const res = await api.post<CatalogRefreshResult>(
    "/api/job-catalog/refresh",
    {},
  );

  if (res.error) {
    throw new CatalogRefreshError(res.error.code, res.error.message);
  }

  if (!res.data) {
    throw new Error("Job refresh returned no data");
  }

  return res.data;
}

export async function fetchCatalogExploredJobs(
  input: CatalogExploreJobsParams = {},
): Promise<CatalogJobDiscoveryResult> {
  const params = new URLSearchParams();

  appendMaybeParam(params, "query", input.query?.trim() || null);
  appendArrayParam(params, "locations", input.locations);
  appendArrayParam(params, "cities", input.cities);
  appendArrayParam(params, "workModes", input.workModes);
  appendArrayParam(params, "employmentTypes", input.employmentTypes);
  appendArrayParam(params, "sources", input.sources);
  appendArrayParam(params, "applyModes", input.applyModes);
  appendMaybeParam(params, "datePosted", input.datePosted ?? null);
  appendMaybeParam(params, "page", input.page ?? null);
  appendMaybeParam(params, "limit", input.limit ?? null);
  appendMaybeParam(
    params,
    "includeLowConfidence",
    input.includeLowConfidence ?? null,
  );
  appendMaybeParam(
    params,
    "surfaceEligibleOnly",
    input.surfaceEligibleOnly ?? null,
  );

  try {
    const res = await api.get<CatalogJobListItem[]>(
      `/api/job-catalog/explore${params.size ? `?${params.toString()}` : ""}`,
    );

    if (res.error) {
      throw new Error(res.error.message);
    }

    return {
      jobs: res.data ?? [],
      ...unwrapMeta(res.meta),
    };
  } catch (error) {
    throw error instanceof Error ? error : new Error("Unable to explore jobs");
  }
}

export async function fetchCatalogJob(
  jobId: string,
): Promise<CatalogJobDetail> {
  const res = await api.get<CatalogJobDetail>(
    `/api/job-catalog/${encodeURIComponent(jobId)}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog job not found");
  }

  return res.data;
}

export async function fetchCatalogApplyHistory(
  limit = 50,
): Promise<CatalogApplicationRecord[]> {
  const res = await api.get<CatalogApplicationRecord[]>(
    `/api/job-catalog/apply-history?limit=${encodeURIComponent(limit)}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  return res.data ?? [];
}

export async function fetchCatalogSavedJobs(
  limit = 50,
): Promise<CatalogJobListItem[]> {
  const res = await api.get<CatalogJobListItem[]>(
    `/api/job-catalog/saved?limit=${encodeURIComponent(limit)}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  return res.data ?? [];
}

export async function saveCatalogJob(
  jobId: string,
): Promise<CatalogJobUserStateSummary> {
  const res = await api.post<CatalogJobUserStateSummary>(
    `/api/job-catalog/${encodeURIComponent(jobId)}/save`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog save returned no state");
  }

  return res.data;
}

export async function unsaveCatalogJob(
  jobId: string,
): Promise<CatalogJobUserStateSummary> {
  const res = await api.post<CatalogJobUserStateSummary>(
    `/api/job-catalog/${encodeURIComponent(jobId)}/unsave`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog unsave returned no state");
  }

  return res.data;
}

export async function hideCatalogJob(
  jobId: string,
): Promise<CatalogJobUserStateSummary> {
  const res = await api.post<CatalogJobUserStateSummary>(
    `/api/job-catalog/${encodeURIComponent(jobId)}/hide`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog hide returned no state");
  }

  return res.data;
}

export async function unhideCatalogJob(
  jobId: string,
): Promise<CatalogJobUserStateSummary> {
  const res = await api.post<CatalogJobUserStateSummary>(
    `/api/job-catalog/${encodeURIComponent(jobId)}/unhide`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog unhide returned no state");
  }

  return res.data;
}

export async function fetchCatalogApplyPreparation(
  jobId: string,
  input: CatalogApplyInput = {},
): Promise<CatalogApplyPreparation> {
  const res = await api.post<CatalogApplyPreparation>(
    `/api/job-catalog/${encodeURIComponent(jobId)}/prepare-apply`,
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog application preparation returned no data");
  }

  return res.data;
}

export async function submitCatalogApply(
  jobId: string,
  input: CatalogApplyInput = {},
): Promise<CatalogApplySubmissionResult> {
  const res = await api.post<CatalogApplySubmissionResult>(
    `/api/job-catalog/${encodeURIComponent(jobId)}/submit-apply`,
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog application submission returned no data");
  }

  return res.data;
}

export async function trackCatalogRedirect(
  jobId: string,
  input: CatalogTrackRedirectInput = {},
): Promise<CatalogApplicationRecord> {
  const res = await api.post<CatalogApplicationRecord>(
    `/api/job-catalog/${encodeURIComponent(jobId)}/track-redirect`,
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog redirect tracking returned no data");
  }

  return res.data;
}

export async function confirmCatalogApply(
  jobId: string,
  input: CatalogConfirmApplyInput,
): Promise<CatalogApplicationRecord> {
  const res = await api.post<CatalogApplicationRecord>(
    `/api/job-catalog/${encodeURIComponent(jobId)}/confirm-apply`,
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Catalog apply confirmation returned no data");
  }

  return res.data;
}

export async function fetchAutoApplyOverview(): Promise<AutoApplyOverview> {
  const res = await api.get<AutoApplyOverview>("/api/job-catalog/auto-apply");

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Auto-apply overview returned no data");
  }

  return res.data;
}

type AutoApplyActivityPayload =
  | AutoApplyActivityRecord[]
  | {
      activity?: AutoApplyActivityRecord[];
      nextCursor?: string | null;
      cursor?: string | null;
      items?: AutoApplyActivityRecord[];
    }
  | AutoApplyOverview;

function normalizeAutoApplyActivityPayload(
  payload: AutoApplyActivityPayload | null | undefined,
  nextCursor: string | null,
): { activity: AutoApplyActivityRecord[]; nextCursor: string | null } | null {
  if (!payload) {
    return null;
  }

  if (Array.isArray(payload)) {
    return { activity: payload, nextCursor };
  }

  if ("activity" in payload && Array.isArray(payload.activity)) {
    const metaPayload = payload as {
      nextCursor?: string | null;
      cursor?: string | null;
    };
    return {
      activity: payload.activity,
      nextCursor:
        typeof metaPayload.nextCursor === "string"
          ? metaPayload.nextCursor
          : typeof metaPayload.cursor === "string"
            ? metaPayload.cursor
            : nextCursor,
    };
  }

  if ("items" in payload && Array.isArray(payload.items)) {
    return { activity: payload.items, nextCursor };
  }

  return null;
}

export async function fetchAutoApplyActivity(
  limit = 10,
  cursor?: string,
): Promise<{ activity: AutoApplyActivityRecord[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);

  try {
    const res = await api.get<AutoApplyActivityPayload>(
      `/api/job-catalog/auto-apply/activity${params.size ? `?${params.toString()}` : ""}`,
    );

    if (res.error) {
      throw new Error(res.error.message);
    }

    const normalized = normalizeAutoApplyActivityPayload(
      res.data,
      (res as any).meta?.nextCursor ?? null,
    );

    if (normalized) {
      return normalized;
    }
  } catch {
    // Fall back to the overview payload when a dedicated activity endpoint is unavailable.
  }

  const overview = await fetchAutoApplyOverview();
  return {
    activity: overview.activity.slice(0, limit),
    nextCursor: null,
  };
}

export async function dismissAutoApplyActivity(
  activityId: string,
): Promise<void> {
  const res = await api.post(
    `/api/job-catalog/auto-apply/activity/${encodeURIComponent(activityId)}/dismiss`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }
}

export async function updateAutoApplyRule(
  ruleKey: string,
  input: UpdateAutoApplyRuleInput,
): Promise<AutoApplyRuleSummary> {
  const res = await api.post<AutoApplyRuleSummary>(
    `/api/job-catalog/auto-apply/rules/${encodeURIComponent(ruleKey)}`,
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Auto-apply rule update returned no data");
  }

  return res.data;
}

export async function runAutoApply(
  ruleKey: string,
): Promise<AutoApplyOverview> {
  const res = await api.post<AutoApplyOverview>(
    "/api/job-catalog/auto-apply/run",
    {
      ruleKey,
    },
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Auto-apply run returned no data");
  }

  return res.data;
}

export async function fetchDiscoveredJobs(
  input: DiscoverJobsParams = {},
): Promise<JobDiscoveryItem[]> {
  const params = new URLSearchParams();

  if (input.query?.trim()) {
    params.set("query", input.query.trim());
  }

  appendArrayParam(params, "locations", input.locations);
  appendArrayParam(params, "workModes", input.workModes);
  appendArrayParam(params, "employmentTypes", input.employmentTypes);

  if (input.datePosted) {
    params.set("datePosted", input.datePosted);
  }

  if (input.page) {
    params.set("page", String(input.page));
  }

  if (input.limit) {
    params.set("limit", String(input.limit));
  }

  const res = await api.get<JobDiscoveryItem[]>(
    `/api/jobs/discover${params.size ? `?${params.toString()}` : ""}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  return res.data ?? [];
}

export async function fetchJobApplications(
  input: FetchApplicationsParams = {},
): Promise<JobApplicationSummary[]> {
  const params = new URLSearchParams();

  if (input.jobId) {
    params.set("jobId", input.jobId);
  }

  if (input.limit) {
    params.set("limit", String(input.limit));
  }

  const res = await api.get<JobApplicationSummary[]>(
    `/api/jobs/applications${params.size ? `?${params.toString()}` : ""}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  return res.data ?? [];
}

export async function fetchJob(jobId: string): Promise<CompanyJobSummary> {
  const res = await api.get<CompanyJobSummary>(`/api/jobs/${jobId}`);

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Job not found");
  }

  return res.data;
}

export async function applyToJob(
  jobId: string,
  input: ApplyToJobInput,
): Promise<JobApplicationSummary> {
  const res = await api.post<JobApplicationSummary>(
    `/api/jobs/${jobId}/apply`,
    input,
  );

  if (res.error) {
    // Keep the code: the apply sheets translate the known ones (X4).
    throw new ApplyRequestError(
      res.error.code,
      res.error.message,
      res.error.details,
    );
  }

  if (!res.data) {
    throw new Error("Application submission returned no data");
  }

  return res.data;
}

export async function fetchCompanyJobs(
  companyId: string,
  limit = 50,
): Promise<CompanyJobListResult> {
  const res = await api.get<CompanyJobListResult>(
    `/api/jobs/company?companyId=${encodeURIComponent(companyId)}&limit=${limit}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  return res.data ?? { jobs: [], total: 0 };
}

export async function createCompanyJob(
  input: CreateCompanyJobInput,
): Promise<CompanyJobSummary> {
  const res = await api.post<CompanyJobSummary>("/api/jobs", input);

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Job creation returned no data");
  }

  return res.data;
}

export async function closeCompanyJob(
  jobId: string,
): Promise<CompanyJobSummary> {
  const res = await api.post<CompanyJobSummary>(
    `/api/jobs/company/${encodeURIComponent(jobId)}/close`,
    {},
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Job close returned no data");
  }

  return res.data;
}

export async function generateCompanyJobDraft(
  input: GenerateCompanyJobDraftInput,
): Promise<GenerateCompanyJobDraftResultWithV2> {
  const res = await api.post<GenerateCompanyJobDraftResultWithV2>(
    "/api/jobs/generate-draft",
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Job draft generation returned no data");
  }

  return res.data;
}

// ── Employer job contract v2 (PRD §7) ────────────────────────────────────────

/** POST /api/jobs with the four-step v2 body (draft or live). */
export async function createEmployerJob(
  input: CreateEmployerJobInput,
): Promise<CompanyJobWithV2> {
  const res = await api.post<CompanyJobWithV2>("/api/jobs", input);

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Job creation returned no data");
  }

  return res.data;
}

export async function updateEmployerJob(
  jobId: string,
  input: UpdateEmployerJobInput,
): Promise<CompanyJobWithV2> {
  const res = await api.patch<CompanyJobWithV2>(
    `/api/jobs/company/${encodeURIComponent(jobId)}`,
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Job update returned no data");
  }

  return res.data;
}

async function postEmployerJobLifecycle(
  jobId: string,
  action: "publish" | "offline" | "reopen" | "archive",
  body: Record<string, unknown> = {},
): Promise<CompanyJobWithV2> {
  const res = await api.post<CompanyJobWithV2>(
    `/api/jobs/company/${encodeURIComponent(jobId)}/${action}`,
    body,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error(`Job ${action} returned no data`);
  }

  return res.data;
}

export function publishEmployerJob(jobId: string): Promise<CompanyJobWithV2> {
  return postEmployerJobLifecycle(jobId, "publish");
}

export function offlineEmployerJob(jobId: string): Promise<CompanyJobWithV2> {
  return postEmployerJobLifecycle(jobId, "offline");
}

export function reopenEmployerJob(
  jobId: string,
  distribution?: JobDistribution,
): Promise<CompanyJobWithV2> {
  return postEmployerJobLifecycle(
    jobId,
    "reopen",
    distribution ? { distribution } : {},
  );
}

export function archiveEmployerJob(jobId: string): Promise<CompanyJobWithV2> {
  return postEmployerJobLifecycle(jobId, "archive");
}

export async function fetchEmployerApplicants(
  jobId: string,
  input: FetchEmployerApplicantsParams = {},
): Promise<EmployerApplicantListResult> {
  const params = new URLSearchParams();

  if (input.query?.trim()) {
    params.set("query", input.query.trim());
  }

  appendArrayParam(params, "statuses", input.statuses);

  if (input.page) {
    params.set("page", String(input.page));
  }

  if (input.pageSize) {
    params.set("pageSize", String(input.pageSize));
  }

  const res = await api.get<EmployerApplicantListResult>(
    `/api/jobs/company/${jobId}/applicants${params.size ? `?${params.toString()}` : ""}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Applicant list returned no data");
  }

  return res.data;
}

export async function fetchEmployerApplicantDetail(
  jobId: string,
  applicationId: string,
): Promise<EmployerApplicantDetail> {
  const res = await api.get<EmployerApplicantDetail>(
    `/api/jobs/company/${jobId}/applicants/${applicationId}`,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Applicant detail returned no data");
  }

  return res.data;
}

export async function updateEmployerApplicantStatus(
  applicationId: string,
  input: EmployerApplicationStatusUpdateInput,
): Promise<EmployerApplicantDetail> {
  const res = await api.post<EmployerApplicantDetail>(
    `/api/jobs/company/applications/${applicationId}/status`,
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Applicant status update returned no data");
  }

  return res.data;
}

export async function sendEmployerApplicantMessage(
  applicationId: string,
  input: EmployerApplicantMessageInput,
): Promise<{
  conversation: unknown;
  message: unknown;
}> {
  const res = await api.post<{ conversation: unknown; message: unknown }>(
    `/api/jobs/company/applications/${applicationId}/message`,
    input,
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  if (!res.data) {
    throw new Error("Applicant message returned no data");
  }

  return res.data;
}
